/**
 * Types and KPI summary for the Contract Coverage Intelligence module.
 *
 * Source: "R - contract and MSA analysis.xlsx" (MSA tab + Download contracts tab).
 * Parent supplier rows are loaded from GET /api/showcase/contract_coverage/.
 *
 * Suppliers are aggregated to PARENT level (spend, transactions, entities summed).
 * Contracts are matched to individual child suppliers by normalized supplier name;
 * contracts matching only a parent name are attached to that parent's largest child.
 * Annual contract values are converted to EUR with the illustrative rates below.
 *
 * Each child gets a contract position:
 *   contract_required           - no contract; mandatory          -> 6% of spend (saving)
 *   contract_recommended        - no contract; advisable          -> 6% of spend (saving)
 *   above_contracted_value      - spend exceeds contracted value  -> 6% of the excess (saving)
 *   below_expected_utilization  - used below the expected range    -> 2% of the unused part (avoidance)
 *   within_expected_utilization - utilization within range         -> none
 *   no_contract_required        - low-criticality supplier         -> none
 *
 * "Within expected" means utilization between 80% and 100% of the cap.
 * Parent cost saving / avoidance are the sums of their children.
 *
 * GENERATED by tools/genContractCoverageData.py — do not edit by hand.
 */

export type ContractPosition =
    | 'contract_required'
    | 'contract_recommended'
    | 'above_contracted_value'
    | 'below_expected_utilization'
    | 'within_expected_utilization'
    | 'no_contract_required';

export type ContractChild = {
    supplier: string;
    country: string | null;
    spend: number;
    transactions: number;
    entities: number;
    activeMonths: number;
    // Component scores (0..5) that feed the weighted score — seed the override UI
    criticalityScore: number;
    dependencyScore: number;
    fragmentationScore: number;
    negotiationScore: number;
    weightedScore: number;       // 0..5, drives the contract decision threshold
    position: ContractPosition;
    contractedValueEur: number;   // 0 if no contract / value not captured
    utilizationPct: number;       // cumulative spend / contracted value (%)
    costSavingEur: number;
    costAvoidanceEur: number;
    contractCount: number;
    nextExpiration: string | null;
};

export type ContractParent = {
    id: string;
    name: string;
    country: string | null;
    spend: number;
    transactions: number;
    entities: number;
    childCount: number;
    contractedChildren: number;
    costSavingEur: number;        // sum of children
    costAvoidanceEur: number;     // sum of children
    children: ContractChild[];
};

export type CoverageKpis = {
    suppliersAnalysed: number;
    parentsAnalysed: number;
    contractRequired: number;
    contractRecommended: number;
    requiredSpend: number;
    recommendedSpend: number;
    aboveContracted: number;
    belowUtilization: number;
    withinUtilization: number;
    noContractRequired: number;
    contractedSuppliers: number;
    totalSpend: number;
    costSavingEur: number;
    costAvoidanceEur: number;
    currency: string;
};

export const COVERAGE_CURRENCY = 'EUR';

export const COVERAGE_KPIS: CoverageKpis = {
    "suppliersAnalysed": 9988,
    "parentsAnalysed": 6669,
    "contractRequired": 639,
    "contractRecommended": 2197,
    "requiredSpend": 488856535,
    "recommendedSpend": 663747195,
    "aboveContracted": 13,
    "belowUtilization": 14,
    "withinUtilization": 17,
    "noContractRequired": 7108,
    "contractedSuppliers": 44,
    "totalSpend": 1402690189,
    "costSavingEur": 69885299,
    "costAvoidanceEur": 117240,
    "currency": "EUR"
};

export const COVERAGE_SUBTITLE =
    'Suppliers aggregated to parent level. Each child supplier is matched against its contracts and assigned a contract position; parents carry the summed cost saving and cost avoidance.';